import { useState, useEffect } from 'react';
import { Modal, Carousel } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import MediaFetcher from '../../../ux/Creatives/library/MediaFetcher';

// Add transparent modal styles
const transparentModalStyles = `
  .creative-preview-modal .ant-modal-content {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
  }
  .creative-preview-modal .ant-modal {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

// Inject styles into head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = transparentModalStyles;
  if (!document.head.querySelector('style[data-modal="transparent"]')) {
    styleSheet.setAttribute('data-modal', 'transparent');
    document.head.appendChild(styleSheet);
  }
}

const CreativePreviewModal = ({
  visible,
  onClose,
  images,
  startIndex = 0
}) => {
  const [currentSlide, setCurrentSlide] = useState(startIndex);
  const [currentMedia, setCurrentMedia] = useState(null);

  useEffect(() => {
    if (images && images.length > 0) {
      setCurrentSlide(startIndex);
      setCurrentMedia(images[startIndex]);
    }
  }, [images, startIndex]);

  // Cleanup videos when modal closes
  useEffect(() => {
    if (!visible) {
      const videos = document.querySelectorAll('video');
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
    }
  }, [visible]);

  // Glassmorphism arrow components for carousel
  const PrevArrow = ({ onClick }) => (
    <div
      style={{
        position: "absolute",
        left: "50px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
        borderRadius: "50%",
        width: "48px",
        height: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "white",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        transition: "all 0.2s ease"
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15))";
        e.currentTarget.style.transform = "translateY(-50%) scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))";
        e.currentTarget.style.transform = "translateY(-50%) scale(1)";
      }}
    >
      <LeftOutlined style={{ fontSize: "16px", fontWeight: "bold" }} />
    </div>
  );

  const NextArrow = ({ onClick }) => (
    <div
      style={{
        position: "absolute",
        right: "50px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
        borderRadius: "50%",
        width: "48px",
        height: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "white",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        transition: "all 0.2s ease"
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15))";
        e.currentTarget.style.transform = "translateY(-50%) scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))";
        e.currentTarget.style.transform = "translateY(-50%) scale(1)";
      }}
    >
      <RightOutlined style={{ fontSize: "16px", fontWeight: "bold" }} />
    </div>
  );

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="80vw"
      centered
      closable={false}
      mask={true}
      maskStyle={{
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        backdropFilter: "blur(3px)"
      }}
      maskClosable={true}
      style={{
        maxWidth: "900px",
        background: "transparent",
        boxShadow: "none"
      }}
      bodyStyle={{
        padding: "60px 0 40px 0", // Top padding for UI elements, bottom padding for counter
        height: "70vh",
        background: "transparent",
        display: "flex",
        flexDirection: "column"
      }}
      className="creative-preview-modal"
    >
      {/* Ad Unit Name */}
      <div style={{
        position: "absolute",
        top: "10px",
        left: "50px",
        zIndex: 10,
        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
        color: "white",
        padding: "6px 16px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        letterSpacing: "0.5px",
        maxWidth: "300px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}>
        {currentMedia?.name || currentMedia?.key || 'Ad Unit'}
      </div>

      {/* Counter at Bottom Center */}
      <div style={{
        position: "absolute",
        bottom: "10px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10,
        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
        color: "white",
        padding: "6px 12px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        letterSpacing: "0.5px"
      }}>
        {currentSlide + 1} / {images.length}
      </div>

      {/* Minimal Close Button */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          top: "10px",
          right: "50px",
          zIndex: 10,
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
          color: "white",
          width: "32px",
          height: "32px",
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "600",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          transition: "all 0.2s ease",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15))";
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </div>

      {images.length > 0 && (
        <Carousel
          initialSlide={startIndex}
          arrows={images.length > 1}
          prevArrow={<PrevArrow />}
          nextArrow={<NextArrow />}
          dots={false}
          infinite={images.length > 1}
          afterChange={(currentSlideIndex) => {
            setCurrentSlide(currentSlideIndex);
            setCurrentMedia(images[currentSlideIndex]);
          }}
          style={{ height: "100%", flex: 1 }}
        >
          {images.map((media, index) => (
            <div key={media.id || media.key}>
              <div style={{
                height: "calc(70vh - 100px)", // Subtract space for top and bottom UI
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 120px" // Space for arrows
              }}>
                {/* Use MediaFetcher for everything - force remount on slide change */}
                <MediaFetcher
                  key={`media-${media.key}-${index}-${currentSlide}`}
                  mediaKey={media.key}
                  width="100%"
                  height="100%"
                  objectFit="contain"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: "8px",
                    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.4)"
                  }}
                />
              </div>
            </div>
          ))}
        </Carousel>
      )}
    </Modal>
  );
};

export default CreativePreviewModal;